import React, { useState, useEffect, useContext, useMemo } from "react";
import {
  Stack,
  Heading,
  Checkbox,
  CheckboxGroup,
  RadioGroup,
  Radio,
  TextAreaField,
  Alert,
  Text,
  TextLink,
  List,
  ListItem,
  Divider,
} from "@origin-digital/ods-core";
import FormFieldLayout from "../FormComponents/FormFieldLayout";
import {
  FormikProps,
  Field,
  useFormikContext,
  FieldProps,
  FieldInputProps,
  yupToFormErrors,
} from "formik";
import { ChangeRequestFormValues } from "./formValue";
import GeneralTextField from "../FormComponents/GeneralTextField";
import DatePickerField from "../FormComponents/DatePickerField";
import { DocumentTypeEnum, ErrorTypeEnum } from "../types";
import ElectricianSection from "./ElectricianSection";
import { changeRequestElectricityValidationSchema } from "./validationSchema";
import ChangeRequestContext from "src/context/ChangeRequestContext";
import {
  getShowFieldsForChangeTypes,
  typeOfChangesOptions,
  offPeakOptions,
  ChangeElectricityJobType,
  mapFormikValuesToDraftService,
} from "./helpers";
import { apiClient } from "src/api/apiService";
import { getErrorTypeEnum } from "src/api/apiHelper";
import { useNavigate } from "react-router-dom";
import { useFieldVisibilityReset } from "./hooks/useFieldVisibilityReset";
import PermissionsActionButtons from "../PermissionsActionButtons";
import { ChangeRequestFormFileUploadFormikField } from "./ChangeRequestFormFileUploadFormikField";
import { State } from "src/components/Changes";

const ChangeRequestElectricityBlock: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { values, errors, setErrors, setFieldValue } =
    useFormikContext<ChangeRequestFormValues>();
  const {
    setDisplayingAlertBanner,
    setDisplayingSnackbar,
    setSectionVisibility,
    sectionVisibility,
    draftInfo,
    setDraftInfo,
  } = useContext(ChangeRequestContext);
  const changeTypesError = errors?.changeTypes;
  const state = values.premise?.site_details?.state ?? "";
  const navigate = useNavigate();

  const showFields = useMemo(
    () =>
      getShowFieldsForChangeTypes({
        state,
        changeTypes: values.changeTypes,
      }),
    [state, values.changeTypes]
  );

  //Reset the fields to the initial value when the field visibility changes
  useFieldVisibilityReset({ showFields, setFieldValue });

  useEffect(() => {
    setSectionVisibility({
      meteringRequirementsSection:
        showFields.showMeterProvider || showFields.showOffPeakRequirements,
      supportingDocumentsSection:
        showFields.showRexId ||
        showFields.showCoesReferenceNumber ||
        showFields.showCoesFiles ||
        showFields.showEwrFiles,
      solarInstallationDetailsSection:
        showFields.showSolarInstallationDetails ||
        showFields.showInstallationDate,
    });
  }, [showFields]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      //Check all the fields within the whole form. If any field has an error, display the error banner.
      setDisplayingAlertBanner(ErrorTypeEnum.OCP_FIELD_VALIDATION);
    } else {
      setDisplayingAlertBanner(null);
    }
  }, [errors]);

  const saveDraft = async () => {
    try {
      setErrors({});
      setIsLoading(true);
      const { data: draftData } = await apiClient.post("/draft/premise", {
        premise: values.premise,
        services: [
          {
            draft_id: draftInfo?.draft_id,
            draft_version: draftInfo?.draft_version,
            request_id: draftInfo?.request_id,
            service_type: "ELECTRICITY",
            request_type: "CHANGE_SUPPLY",
            payload: mapFormikValuesToDraftService(values),
            nmi_mirn: values.nmi,
          },
        ],
      });
      setDraftInfo({
        draft_id: draftData.services[0].draft_id,
        draft_version: draftData.services[0].draft_version,
        request_id: draftData.services[0].request_id,
        raw_request_status: draftData.services[0].raw_request_status,
        request_type: draftData.services[0].request_type,
        message_enabled: draftData.services[0].message_enabled,
      });
      setDisplayingSnackbar({
        message: `Draft has been saved.`,
        tone: "positive",
      });
    } catch (e: any) {
      setDisplayingSnackbar({
        message: `Couldn't save draft`,
        tone: "caution",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const submitRequest = async () => {
    setDisplayingSnackbar(null);
    setIsLoading(true);
    try {
      const response = await apiClient.put(
        `/submission/${draftInfo?.request_id}`
      );

      setDisplayingAlertBanner(null);

      navigate("/changes", {
        state: {
          submitted: true,
          journeyId: response.data.services[0].journey_id,
        },
      });
    } catch (e: any) {
      setDisplayingAlertBanner(getErrorTypeEnum(e) ?? null);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* ===== Change type section ===== */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="changeTypeSection"
      >
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Change type</Heading>
          <CheckboxGroup
            id="changeTypes"
            label="Type of changes required"
            error={!!changeTypesError}
            helperText="Select all that apply. You must select at least one."
          >
            {typeOfChangesOptions.map((option) => (
              <Field
                type="checkbox"
                value={option.value}
                name="changeTypes"
                key={option.value}
              >
                {({ field }: { field: FormikProps<FieldProps> }) => (
                  <Checkbox
                    {...field}
                    key={option.value}
                    id={option.value}
                    label={option.label}
                  />
                )}
              </Field>
            ))}
          </CheckboxGroup>
        </FormFieldLayout>
      </Stack>

      {/* ===== Licensed electrician section ===== */}
      <ElectricianSection noCompany={!!values.noCompany} />
      {/* ===== Supply details section ===== */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="supplyDetailsSection"
      >
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Supply details</Heading>
          {showFields.showSupplyType && (
            <Field name="supplyDetails.supplyType">
              {({ field }: { field: FormikProps<FieldProps> }) => (
                <RadioGroup
                  {...field}
                  id="supplyDetails.supplyType"
                  name="supplyDetails.supplyType"
                  label="Required supply details"
                >
                  <Radio id="overhead" label="Overhead" value="overhead" />
                  <Radio
                    id="underground"
                    label="Underground"
                    value="underground"
                  />
                </RadioGroup>
              )}
            </Field>
          )}
        </FormFieldLayout>

        <FormFieldLayout>
          <Field
            component={GeneralTextField}
            name="supplyDetails.maxLoad"
            label="Max load, Amps"
            type="number"
          />
        </FormFieldLayout>

        <FormFieldLayout multipleFields>
          <Field name="supplyDetails.numberOfPhases">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <RadioGroup
                {...field}
                id="supplyDetails.numberOfPhases"
                name="supplyDetails.numberOfPhases"
                label="Required supply phases"
              >
                <Radio id="singlePhase" label="Single phase" value="1" />
                <Radio id="twoPhase" label="Two phase" value="2" />
                <Radio id="threePhase" label="Three phase" value="3" />
              </RadioGroup>
            )}
          </Field>

          {["2", "3"].includes(values.supplyDetails.numberOfPhases) && (
            <Field type="checkbox" name="supplyDetails.transformer">
              {/* eslint-disable-next-line react/no-unused-prop-types */}
              {({ field }: { field: FieldInputProps<boolean> }) => (
                <Checkbox
                  label="Current transformer (CT metering) required"
                  id="supplyDetails.transformer"
                  checked={field.value}
                  onChange={(e) => field.onChange(e)}
                />
              )}
            </Field>
          )}
        </FormFieldLayout>
      </Stack>

      {/* ===== Metering requirements section ===== */}
      {sectionVisibility.meteringRequirementsSection && (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
          id="meteringRequirementsSection"
        >
          <FormFieldLayout multipleFields>
            <Heading variant="h4">Metering requirements</Heading>
            {showFields.showMeterProvider && (
              <Field name="meteringRequirements.meterProvider">
                {({ field }: { field: FormikProps<FieldProps> }) => (
                  <RadioGroup
                    {...field}
                    id="meteringRequirements.meterProvider"
                    name="meteringRequirements.meterProvider"
                    label="Preferred metering provider"
                  >
                    <Radio id="origin" label="Origin" value="origin" />
                    <Radio
                      id="intellihub"
                      label="IntelliHub"
                      value="intellihub"
                    />
                    <Radio
                      id="blueCurrent"
                      label="BlueCurrent"
                      value="blueCurrent"
                    />
                    <Radio id="plusES" label="Plus ES" value="plusES" />
                  </RadioGroup>
                )}
              </Field>
            )}
          </FormFieldLayout>

          {showFields.showOffPeakRequirements && (
            <FormFieldLayout multipleFields>
              <CheckboxGroup
                id="meteringRequirements.offPeakRequirements"
                label="Off peak requirements"
                error={!!errors?.meteringRequirements?.offPeakRequirements}
                helperText={
                  errors?.meteringRequirements?.offPeakRequirements as string
                }
              >
                {offPeakOptions.map((option) => (
                  <Field
                    type="checkbox"
                    value={option.value}
                    name="meteringRequirements.offPeakRequirements"
                    key={option.value}
                  >
                    {({ field }: { field: FormikProps<FieldProps> }) => (
                      <Checkbox
                        {...field}
                        key={option.value}
                        id={option.value}
                        label={option.label}
                      />
                    )}
                  </Field>
                ))}
              </CheckboxGroup>

              {values.meteringRequirements.offPeakRequirements.includes(
                "other"
              ) ? (
                <Field
                  id="meteringRequirements.otherOffPeakRequirements"
                  name="meteringRequirements.otherOffPeakRequirements"
                >
                  {({ field }: { field: FormikProps<FieldProps> }) => (
                    <TextAreaField
                      {...field}
                      id="otherOffPeakRequirements"
                      value={
                        values.meteringRequirements.otherOffPeakRequirements ??
                        ""
                      }
                      variant="outlined"
                      label="Other off peak requirements"
                      error={
                        !!errors?.meteringRequirements?.otherOffPeakRequirements
                      }
                      helperText={
                        errors?.meteringRequirements?.otherOffPeakRequirements
                      }
                    />
                  )}
                </Field>
              ) : null}
            </FormFieldLayout>
          )}
        </Stack>
      )}

      {/* ===== Meter access section ===== */}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="meterAccessSection"
      >
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Meter access</Heading>
          <Text>
            We'll need clear and safe access to the property and the meter
            location to do this work. If we don't have this we'll need to
            reschedule the work, fees may apply, and the timeframes for meter
            installations will no longer apply.
          </Text>
          <Field
            id="meterAccess.meterLocation"
            name="meterAccess.meterLocation"
          >
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <TextAreaField
                {...field}
                id="meterAccess.meterLocation"
                value={values.meterAccess.meterLocation ?? ""}
                variant="outlined"
                label="Where is the meter located? (Optional)"
                helperText="e.g. on the front wall, on the left side of the garage."
              />
            )}
          </Field>
        </FormFieldLayout>
        {/* End Meter location */}

        {/* Meter behind gate */}
        <FormFieldLayout multipleFields>
          <Field name="meterAccess.meterBehindGate">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <RadioGroup
                {...field}
                id="meterAccess.meterBehindGate"
                name="meterAccess.meterBehindGate"
                label="Is the meter locked behind a gate?"
              >
                <Radio id="no" label="No" value="no" />
                <Radio id="yes" label="Yes" value="yes" />
              </RadioGroup>
            )}
          </Field>
          {/* How do we access the meter field appears here if Yes = true */}
          {values.meterAccess.meterBehindGate === "yes" && (
            <Field
              id="meterAccess.accessInstructions"
              name="meterAccess.accessInstructions"
            >
              {({ field }: { field: FormikProps<FieldProps> }) => (
                <TextAreaField
                  {...field}
                  id="meterAccess.accessInstructions"
                  value={values.meterAccess.accessInstructions ?? ""}
                  variant="outlined"
                  label="How do we access the meter?"
                  helperText={
                    errors?.meterAccess?.accessInstructions ??
                    "e.g. the code is 963076, pick up a key from security."
                  }
                  error={!!errors?.meterAccess?.accessInstructions}
                />
              )}
            </Field>
          )}
          {/* End Meter behind gate */}
        </FormFieldLayout>

        {/* Meter inside property */}
        <FormFieldLayout multipleFields>
          <Field name="meterAccess.meterInsideProperty">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <RadioGroup
                {...field}
                id="meterAccess.meterInsideProperty"
                name="meterAccess.meterInsideProperty"
                label="Is the meter locked inside the property?"
              >
                <Radio id="no" label="No" value="no" />
                <Radio id="yes" label="Yes" value="yes" />
              </RadioGroup>
            )}
          </Field>
          {values.meterAccess.meterInsideProperty === "yes" && (
            <Alert>
              <Text>
                Someone will need to provide access to the meter. The provider
                will contact you to make an appointment.
              </Text>
            </Alert>
          )}
        </FormFieldLayout>

        {/* End Meter inside property */}

        {/* Animals */}
        <FormFieldLayout multipleFields>
          <Field name="meterAccess.dogOnSite">
            {({ field }: { field: FormikProps<FieldProps> }) => (
              <RadioGroup
                {...field}
                id="meterAccess.dogOnSite"
                name="meterAccess.dogOnSite"
                label="Is the meter in an area with dogs or other unrestrained animals?"
              >
                <Radio id="no" label="No" value="no" />
                <Radio id="yes" label="Yes" value="yes" />
              </RadioGroup>
            )}
          </Field>
          {values.meterAccess.dogOnSite === "yes" && (
            <Alert>
              <Text>
                You will need to restrain any dogs or animals when work takes
                place. The provider will contact you to arrange an appointment.
              </Text>
            </Alert>
          )}
        </FormFieldLayout>

        {showFields.showCoordinationOfElectricalWorks && (
          <FormFieldLayout text="If supply work is being completed, is the Meter provider required to be onsite at the same time the supply work is completed?">
            <Field name="meterAccess.coordinationOfElectricalWorks">
              {({ field }: { field: FormikProps<FieldProps> }) => (
                <RadioGroup
                  {...field}
                  id="meterAccess.coordinationOfElectricalWorks"
                  name="meterAccess.coordinationOfElectricalWorks"
                  label="Coordination of electrical works required?"
                >
                  <Radio id="no" label="No" value="no" />
                  <Radio id="yes" label="Yes" value="yes" />
                </RadioGroup>
              )}
            </Field>
          </FormFieldLayout>
        )}

        {/* End Animals */}
      </Stack>

      {/* ===== Solar installation details section ===== */}
      {sectionVisibility.solarInstallationDetailsSection && (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
          id="solarInstallationDetailsSection"
        >
          <FormFieldLayout multipleFields>
            {showFields.showSolarInstallationDetails && (
              <Heading variant="h4">Solar installation details</Heading>
            )}
            {showFields.showInstallationDate && (
              <DatePickerField
                name="solarInstallationDetails.installationDate"
                label="Date panels were installed"
                shouldDisableDate={() => false}
              />
            )}
          </FormFieldLayout>
          {/* End Animals */}
        </Stack>
      )}

      {/* ===== Supporting documents section ===== */}
      {sectionVisibility.supportingDocumentsSection && (
        <Stack
          space={{
            xs: "xlarge",
            md: "xxlarge",
          }}
          id="supportingDocumentsSection"
        >
          <FormFieldLayout multipleFields>
            <Heading variant="h4">Supporting documents</Heading>
            {showFields.showRexId && (
              <Field
                component={GeneralTextField}
                name="supportingDocuments.rexId"
                label="REX ID"
                type="number"
              />
            )}
          </FormFieldLayout>
          {showFields.showCoesReferenceNumber && (
            <FormFieldLayout>
              <Field
                component={GeneralTextField}
                name="supportingDocuments.coesReferenceNumber"
                label="CoES (Certificate of Electrical Safety) reference number"
                type="number"
              />
            </FormFieldLayout>
          )}
          {showFields.showCoesFiles && (
            <FormFieldLayout
              multipleFields
              text="You can upload pdf, doc/docx, jpg/jpeg, png, heif, webp, xls/xlsm/xlsx. files. Files must be less than 5MB. You can upload up to two files."
            >
              <Text weight="medium">
                CoES (Certificate of Electrical Safety)
              </Text>
              <Field name="coesFiles">
                {({ field }: FieldProps) => (
                  <ChangeRequestFormFileUploadFormikField
                    draftId={draftInfo?.draft_id ?? ""}
                    documentTypes={[
                      DocumentTypeEnum.CERTIFICATE_OF_ELECTRICAL_SAFETY,
                      DocumentTypeEnum.ADDITIONAL_CERTIFICATE_OF_ELECTRICAL_SAFETY,
                    ]}
                    field={field}
                  />
                )}
              </Field>
            </FormFieldLayout>
          )}
          {showFields.showEwrFiles && (
            <FormFieldLayout
              multipleFields
              text="You can upload pdf, doc/docx, jpg/jpeg, png, heif, webp, xls/xlsm/xlsx. files. Files must be less than 5MB."
            >
              <Text weight="medium">Electrical works request</Text>
              <Field name="ewrFiles">
                {({ field }: FieldProps) => (
                  <ChangeRequestFormFileUploadFormikField
                    draftId={draftInfo?.draft_id ?? ""}
                    documentTypes={[DocumentTypeEnum.ELECTRICAL_WORKS_REQUEST]}
                    field={field}
                  />
                )}
              </Field>
            </FormFieldLayout>
          )}
          <FormFieldLayout>
            <Divider />
          </FormFieldLayout>
        </Stack>
      )}
      <Stack
        space={{
          xs: "xlarge",
          md: "xxlarge",
        }}
        id="feesProcessAndTimingsSection"
      >
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Your privacy</Heading>
          <Text>
            You consent to Origin's collection, use, holding and disclosure of
            your personal and credit related information in accordance with our
            privacy and credit reporting statements, available at{" "}
            <TextLink
              href="https://www.originenergy.com.au/privacy"
              target="_blank"
            >
              originenergy.com.au/privacy
            </TextLink>
            . Our credit reporting statement explains who we disclose credit
            information to (including service providers overseas) and how this
            could affect your creditworthiness, as well as how you can access,
            correct or complain about it. Please contact us to request a paper
            copy.
          </Text>
        </FormFieldLayout>
        <FormFieldLayout multipleFields>
          <Heading variant="h4">Fees, process and timings</Heading>
          <Stack space="medium">
            <Text weight="medium">Meter change fees</Text>
            <Text>
              Distributors may charge fees for changes to their meters and
              they'll pass on any fees to you directly or to us. If they charge
              us, we'll pass on the fees to the account holder.
            </Text>
            <Text>
              Origin may charge an administration fee of up to $25 (incl GST)
              for coordinating account-holder-initiated requests for changes to
              electricity meters and this will appear on the account holder's
              bill as a 'meter works administration fee'. This fee doesn't apply
              to Origin Standing customers.
            </Text>
            <Text>
              You can find out more details about the current fees at{" "}
              <TextLink
                href="https://www.originenergy.com.au/meterfees"
                target="_blank "
              >
                originenergy.com.au/meterfees
              </TextLink>
              .
            </Text>
          </Stack>
        </FormFieldLayout>
        <FormFieldLayout>
          <Stack space="medium">
            <Text weight="medium">Process and timeframes</Text>
            <Text>
              When we receive your completed paperwork, we'll send you an email
              to confirm the scheduled date for the work. We'll install/replace
              the meter (if required) within {state === State.VIC ? 15 : 20}{" "}
              business days from the date we receive your completed paperwork,
              or if you have a preferred date you can give us a call to discuss.
              This timeframe won't apply if:
            </Text>
            <List space="xxsmall">
              <ListItem>
                you haven't provided the correct paperwork or the paperwork is
                incomplete
              </ListItem>
              <ListItem>
                the meter provider doesn't have clear and safe access to your
                site, meter box and switchboard or they're prevented from
                entering the property
              </ListItem>
              <ListItem>
                the site isn't ready, and all work required at the premises
                isn't yet complete
              </ListItem>
              <ListItem>
                turning off the power at your site will impact the electricity
                supply of other properties
              </ListItem>
            </List>
            <Text>
              For more information about the process and timeframes, head to{" "}
              <TextLink
                href="https://www.originenergy.com.au/meters"
                target="_blank"
              >
                originenergy.com.au/meters
              </TextLink>
              .
            </Text>
          </Stack>
        </FormFieldLayout>
        {/* Actions */}
        <PermissionsActionButtons
          primaryOnClick={() => {
            changeRequestElectricityValidationSchema
              .validate(values, {
                abortEarly: false,
                context: {
                  ...values,
                  state,
                  noCompany: values.noCompany,
                  onlySolar:
                    values.changeTypes.length === 1 &&
                    values.changeTypes?.[0] ===
                      ChangeElectricityJobType.INSTALL_SOLAR,
                  coordinationOfElectricalWorks:
                    values.meterAccess.coordinationOfElectricalWorks,
                  showOffPeakRequirements: showFields.showOffPeakRequirements,
                  showRexId: showFields.showRexId,
                  showCoesReferenceNumber: showFields.showCoesReferenceNumber,
                  showInstallationDate: showFields.showInstallationDate,
                  showCoesFiles: showFields.showCoesFiles,
                  showEwrFiles: showFields.showEwrFiles,
                },
              })
              .then(saveDraft)
              .then(submitRequest)
              .catch((e) => {
                setErrors(yupToFormErrors(e));
              });
          }}
          primarySpinner={isLoading}
          secondaryOnClick={saveDraft}
          secondaryDisabled={false}
          secondarySpinner={isLoading}
        />
      </Stack>
    </Stack>
  );
};

export default ChangeRequestElectricityBlock;
